import { Combobox, Input, LinkButton, Text } from "@ttc3k/trekker";
import { type ComponentProps, useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapsible } from "shared/components";
import { useGeocoding } from "shared/hooks";
import { Flex } from "styled-system/jsx";
import {
  addressSearchValueFromGoogleAddress,
  type AddressSearchResponseValue,
} from "./addressSearchResponse";

type ManualAddressState = {
  line1: string;
  line2: string;
  city: string;
  subdivision: string;
  postalCode: string;
  country: string;
};

const getManualFullAddress = (address: ManualAddressState) =>
  [
    address.line1,
    address.line2,
    address.city,
    address.subdivision,
    address.postalCode,
    address.country,
  ]
    .map(part => part.trim())
    .filter(Boolean)
    .join(", ");

const getManualAddressState = (
  value: AddressSearchResponseValue | null,
): ManualAddressState => ({
  line1: value?.line1 ?? "",
  line2: value?.line2 ?? "",
  city: value?.city ?? "",
  subdivision: value?.subdivision ?? "",
  postalCode: value?.postalCode ?? "",
  country: value?.country ?? "",
});

export type AddressSearchFieldProps = {
  label?: string;
  disabled?: boolean;
  value: AddressSearchResponseValue | null;
  onChange: (value: AddressSearchResponseValue | null) => void;
  /** Matches trekker `Input` `_labelProps` — defaults to Input label styling. */
  _labelProps?: ComponentProps<typeof Text>;
};

export const AddressSearchField = ({
  label,
  disabled = false,
  value,
  onChange,
  _labelProps,
}: AddressSearchFieldProps) => {
  const { t } = useTranslation("booker", { keyPrefix: "QUESTIONNAIRE" });
  const [inputValue, setInputValue] = useState(value?.fullAddress ?? "");
  const [manualAddress, setManualAddress] = useState<ManualAddressState>(
    getManualAddressState(value),
  );
  const [isManualOpen, setIsManualOpen] = useState(false);

  useEffect(() => {
    setInputValue(value?.fullAddress ?? "");
    setManualAddress(getManualAddressState(value));
  }, [value]);

  const coordLng = value?.location?.coordinates?.[0];
  const coordLat = value?.location?.coordinates?.[1];
  const defaultCoordinates = useMemo(
    () =>
      coordLng != null && coordLat != null
        ? { lng: coordLng, lat: coordLat }
        : undefined,
    [coordLng, coordLat],
  );

  const onAddressSelect = useCallback(
    (address: Parameters<typeof addressSearchValueFromGoogleAddress>[0]) => {
      const nextValue = addressSearchValueFromGoogleAddress(address);
      setInputValue(nextValue.fullAddress);
      setManualAddress(getManualAddressState(nextValue));
      onChange(nextValue);
    },
    [onChange],
  );

  const { suggestionsMappedToCombobox, handleSelectAddress } = useGeocoding(
    inputValue,
    defaultCoordinates,
    onAddressSelect,
    undefined,
    value?.fullAddress,
  );

  // Always keep the currently-selected address present in the collection,
  // even if it's absent from the latest autocomplete suggestions (which are
  // biased toward Canada and can fail to resurface a US place on re-query).
  // Without this, the Combobox can't resolve a label for its controlled
  // `value`, which desyncs the displayed input from the address actually
  // stored in `value`.
  const comboboxItems = useMemo(() => {
    if (!value?.placeId) return suggestionsMappedToCombobox;
    if (suggestionsMappedToCombobox.some(item => item.value === value.placeId)) {
      return suggestionsMappedToCombobox;
    }
    return [
      { value: value.placeId, label: value.fullAddress },
      ...suggestionsMappedToCombobox,
    ];
  }, [suggestionsMappedToCombobox, value?.placeId, value?.fullAddress]);

  const { collection, set } = Combobox.useCollection({
    initialItems: comboboxItems,
  });

  useEffect(() => {
    set(comboboxItems);
  }, [set, comboboxItems]);

  const handleManualAddressChange = (
    field: keyof ManualAddressState,
    nextFieldValue: string,
  ) => {
    if (disabled) return;
    const next = { ...manualAddress, [field]: nextFieldValue };
    const fullAddress = getManualFullAddress(next);
    setManualAddress(next);
    setInputValue(fullAddress);

    if (!fullAddress) {
      onChange(null);
      return;
    }

    onChange({
      ...next,
      fullAddress,
      source: "manual",
      location: value?.location,
    });
  };

  return (
    <Flex flexDir="column" gap="100">
      {label && (
        <Text
          visual="smallMedium"
          color="text.mid"
          whiteSpace="pre-wrap"
          {..._labelProps}
        >
          {label}
        </Text>
      )}
      <Combobox.Root
        collection={collection}
        value={value?.placeId ? [value.placeId] : []}
        inputValue={inputValue}
        onInputValueChange={details => setInputValue(details.inputValue)}
        onValueChange={details => {
          void handleSelectAddress(details);
        }}
        placeholder={t("ADDRESS_SEARCH.PLACEHOLDER")}
        openOnClick
        allowCustomValue
        disabled={disabled}
      >
        <Combobox.ItemGroup>
          {suggestionsMappedToCombobox.length === 0 && (
            <Combobox.ItemGroupLabel>
              {t("ADDRESS_SEARCH.AUTOCOMPLETE_HINT")}
            </Combobox.ItemGroupLabel>
          )}
          {suggestionsMappedToCombobox.map(item => (
            <Combobox.Item key={item.value} item={item} {...item} />
          ))}
        </Combobox.ItemGroup>
      </Combobox.Root>
      <LinkButton
        width="max"
        fontSize="xs"
        type="button"
        disabled={disabled}
        onClick={() => setIsManualOpen(open => !open)}
      >
        {isManualOpen
          ? t("ADDRESS_SEARCH.MANUAL.CLOSE")
          : t("ADDRESS_SEARCH.MANUAL.OPEN")}
      </LinkButton>
      <Collapsible open={isManualOpen}>
        <Flex flexDir="column" gap="150">
          <Input
            label={t("ADDRESS_SEARCH.MANUAL.LINE_1")}
            value={manualAddress.line1}
            disabled={disabled}
            onChange={e => handleManualAddressChange("line1", e.target.value)}
          />
          <Input
            label={t("ADDRESS_SEARCH.MANUAL.LINE_2")}
            value={manualAddress.line2}
            onChange={e => handleManualAddressChange("line2", e.target.value)}
          />
          <Flex
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap="150"
          >
            <Input
              label={t("ADDRESS_SEARCH.MANUAL.CITY")}
              value={manualAddress.city}
              onChange={e => handleManualAddressChange("city", e.target.value)}
            />
            <Input
              label={t("ADDRESS_SEARCH.MANUAL.SUBDIVISION")}
              value={manualAddress.subdivision}
              onChange={e =>
                handleManualAddressChange("subdivision", e.target.value)
              }
            />
          </Flex>
          <Flex
            flexDir={{
              base: "column",
              md: "row",
            }}
            gap="150"
          >
            <Input
              label={t("ADDRESS_SEARCH.MANUAL.POSTAL_CODE")}
              value={manualAddress.postalCode}
              onChange={e =>
                handleManualAddressChange("postalCode", e.target.value)
              }
            />
            <Input
              label={t("ADDRESS_SEARCH.MANUAL.COUNTRY")}
              value={manualAddress.country}
              onChange={e =>
                handleManualAddressChange("country", e.target.value)
              }
            />
          </Flex>
        </Flex>
      </Collapsible>
    </Flex>
  );
};
